import React, { useRef } from 'react';
import { Box, Center, Image, Text } from '@mantine/core';
import { IconPhoto } from '@tabler/icons-react';
import toast from 'react-hot-toast';

const ImageUploadWithPreview = ({
  selectedFile,
  setSelectedFile,
  existingImagePath,
  setExistingImagePath = () => {},   // <= Add this default
  label = "(150x150)Px",
  fullWidthPreview = false, 
}) => {
  const fileInputRef = useRef(null);

  const renderImagePreview = () => {
    if (selectedFile) return URL.createObjectURL(selectedFile);
    if (existingImagePath) return existingImagePath;
    return null;
  };

  const handleFileChange = (e) => {
    const file = e.currentTarget.files[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    const maxSize = 500 * 1024; // 500KB

    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPG, JPEG, Webp, or PNG formats are allowed.');
      return;
    }

    if (file.size > maxSize) {
      toast.error('File size must be 500KB or less.');
      return;
    }

    setSelectedFile(file);
    setExistingImagePath(null);
  };

  const handleImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine the style based on the fullWidthPreview prop
  const containerStyles = (theme) =>
    fullWidthPreview
      ? {
        width: '100%',
        aspectRatio: '16/9',
        border: `2px dashed ${theme.colors.gray[4]}`,
        borderRadius: theme.radius.md,
        cursor: 'pointer',
        backgroundColor: theme.colors.gray[1],
      }
      : {
        width: 150,
        height: 150,
        border: `2px dashed ${theme.colors.gray[4]}`,
        borderRadius: theme.radius.md,
        cursor: 'pointer',
        backgroundColor: theme.colors.gray[1],
      };

  return (
    <>
      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Box mt="md">
        <Text color="cyan.10" size="md" fw={500} mb={4}>
          {label}
        </Text>
        {renderImagePreview() ? (
          <>
            <Box
              sx={(theme) => ({
                ...(
                  fullWidthPreview
                    ? { width: '100%', aspectRatio: '16/9' }
                    : { width: 150, height: 150 }
                ),
                border: `2px dashed ${theme.colors.gray[4]}`,
                borderRadius: theme.radius.md,
                cursor: 'pointer',
                overflow: 'hidden',
              })}
              onClick={handleImageClick}
            >
              <Image
                src={renderImagePreview()}
                alt="Preview"
                width="100%"
                height="100%"
                fit="cover"
              />
            </Box>
            <Box mt="xs">
              <Text
                color="blue"
                size="xs"
                style={{ cursor: 'pointer', textDecoration: 'underline' }}
                onClick={() => window.open(renderImagePreview(), '_blank')}
              >
                View full image
              </Text>
            </Box>
          </>
        ) : (
          <Center sx={containerStyles} mt="sm" onClick={handleImageClick}>
            <IconPhoto size={100} color="#888" />
          </Center>
        )}
      </Box>
    </>
  );
};

export default ImageUploadWithPreview;
